"use client";

import { cn, Skeleton } from "@workspace/frontend-ui";
import getSiteInfo from "@workspace/nextjs_utils/src/components/LinkComponent/helpers/getSiteInfo";
import { type FC, useMemo, useRef, useState } from "react";
import { CATEGORY_VALUES } from "../../constants";
import type {
  GameFragment,
  ProviderFragment,
  SelectedProvider,
} from "../../types";
import CategoryToggles from "./CategoryToggles";
import GameGridItem from "./GameGridItem";
import countGamesByProvider from "./helpers/countGamesByProvider";
import filterGamesByProviders from "./helpers/filterGamesByProviders";
import useRecentSearches from "./hooks/useRecentSearches";
import useScrollState from "./hooks/useScrollState";
import useSearchModalView from "./hooks/useSearchModalView";
import useTranslations from "./hooks/useTranslations";
import LoadMoreButton from "./LoadMoreButton";
import ProviderFilterChips from "./ProviderFilterChips";
import RecentSearches from "./RecentSearches";
import SearchEmptyState from "./SearchEmptyState";
import SearchHeaderBar from "./SearchHeaderBar";
import SearchModalShell from "./SearchModalShell";
import SearchResultsHeader from "./SearchResultsHeader";

type Props = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  games?: GameFragment[];
  isLoading?: boolean;
  onSearch?: (query: string) => void;
  onCategoryChange?: (category: string) => void;
  onSurpriseMe?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  totalSearchResultsCount?: number;
  enabledCategories?: string[];
  locale: string;
  isSearchView: boolean;
  toggleProvider: (provider: SelectedProvider) => void;
  currentSelectedProviders?: SelectedProvider[];
  allProviders?: ProviderFragment[];
};

const SearchModal: FC<Props> = ({
  open = true,
  onOpenChange,
  games = [],
  isLoading = false,
  onSearch,
  onCategoryChange,
  onSurpriseMe,
  onLoadMore,
  hasMore = false,
  totalSearchResultsCount,
  enabledCategories = CATEGORY_VALUES,
  locale = "en",
  isSearchView,
  toggleProvider,
  currentSelectedProviders,
  allProviders,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isBridgeSite] = useState(() => getSiteInfo().isBridgeSite);

  const { recentSearches, saveSearch, removeSearch } = useRecentSearches(open);
  const {
    search,
    category,
    activeProviderFilter,
    scrollContainer,
    setScrollContainer,
    handleSearchChange,
    handleSearchBlur,
    handleClearSearch,
    selectRecentSearch,
    setCategory,
    toggleProviderTab,
    clearProviderTab,
  } = useSearchModalView({
    open,
    currentSelectedProviders,
    onSearch,
    onCategoryChange,
    onSaveRecentSearch: saveSearch,
  });
  const { isTopScrolled, showGradient } = useScrollState(
    scrollContainer,
    hasMore,
    games,
    isLoading,
  );

  const { t, badgeLabels } = useTranslations(locale);

  const [topGamesCount, searchResultsCount] = useMemo(() => {
    return [
      games.filter((item) => item.__type === "topgame").length,
      games.filter((item) => item.__type === "search" || !item.__type).length,
    ];
  }, [games]);

  const [topGames, searchResults] = useMemo(() => {
    const filteredGames = filterGamesByProviders(games, activeProviderFilter);
    const toGridItem = (item: GameFragment) => (
      <GameGridItem
        key={item.id}
        item={item}
        isBridgeSite={isBridgeSite}
        translations={badgeLabels}
      />
    );
    return [
      filteredGames.filter((item) => item.__type === "topgame").map(toGridItem),
      filteredGames
        .filter((item) => item.__type === "search" || !item.__type)
        .map(toGridItem),
    ];
  }, [games, badgeLabels, isBridgeSite, activeProviderFilter]);

  const showEmptyState = isSearchView && !isLoading && searchResultsCount === 0;

  const categoryLabels: Record<string, string> = {
    all: t("casino.search.all"),
    "video-slots": t("casino.search.slots"),
    "live-dealer": t("casino.search.live-casino"),
    originals: t("casino.search.originals"),
  };

  const isResultsView = isSearchView && !showEmptyState;
  const providersStats = useMemo(
    () => countGamesByProvider(games, isSearchView),
    [games, isSearchView],
  );

  const resultsTitle = isResultsView
    ? t("casino.search.new_results")
    : t("casino.search.top_search_games");
  const resultsCount =
    isSearchView && totalSearchResultsCount != null
      ? totalSearchResultsCount
      : topGamesCount;

  return (
    <SearchModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={t("casino.search_games")}
      inputRef={inputRef}
    >
      <SearchHeaderBar
        search={search}
        onSearchChange={handleSearchChange}
        onSearchBlur={handleSearchBlur}
        onClearSearch={handleClearSearch}
        searchPlaceholder={t("casino.search_games")}
        clearSearchLabel={t("casino.clear_search")}
        showSurpriseMe={!showEmptyState}
        surpriseMeLabel={t("casino.search.surprise_me")}
        onSurpriseMe={onSurpriseMe}
        isTopScrolled={isTopScrolled}
        inputRef={inputRef}
        toggleProvider={toggleProvider}
        enabledProviders={currentSelectedProviders}
        allProviders={allProviders}
        closeLabel={t("casino.search.close")}
      />

      {recentSearches.length > 0 && !isSearchView && (
        <RecentSearches
          terms={recentSearches}
          onRemove={removeSearch}
          onSelect={selectRecentSearch}
        />
      )}

      <SearchResultsHeader
        title={resultsTitle}
        count={resultsCount}
        showCount={!isLoading && !showEmptyState}
      />

      {!currentSelectedProviders?.length && (
        <CategoryToggles
          category={category}
          enabledCategories={enabledCategories}
          categoryLabels={categoryLabels}
          onCategoryChange={setCategory}
        />
      )}

      {!showEmptyState && Boolean(currentSelectedProviders?.length) && (
        <ProviderFilterChips
          providers={currentSelectedProviders ?? []}
          activeProviderFilter={activeProviderFilter}
          providerCounts={providersStats}
          allCount={isSearchView ? searchResultsCount : topGamesCount}
          onToggleTab={toggleProviderTab}
          onClearTab={clearProviderTab}
          onRemoveProvider={toggleProvider}
        />
      )}

      <div className="relative flex-1 min-h-0">
        <div
          ref={setScrollContainer}
          className="h-full overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pb-4"
        >
          {showEmptyState && (
            <SearchEmptyState
              title={t("general.search.results_none")}
              subtitle={t("casino.search.not_found_subtitle")}
              surpriseMeLabel={t("casino.search.surprise_me")}
              onSurpriseMe={onSurpriseMe}
            />
          )}
          <div className="grid grid-cols-3 gap-2 md:grid-cols-5! xl:grid-cols-7! [@media(min-width:1440px)]:grid-cols-8!">
            {isLoading
              ? Array.from({ length: 32 }, (_, i) => (
                  <Skeleton key={i} className="w-full aspect-3/4 rounded-lg" />
                ))
              : searchResultsCount === 0 || !isSearchView
                ? topGames
                : searchResults}
          </div>
          {hasMore && isSearchView && !showEmptyState && (
            <LoadMoreButton
              label={t("casino.search.load_more")}
              isTopScrolled={isTopScrolled}
              scrollContainer={scrollContainer}
              onLoadMore={() => onLoadMore?.()}
            />
          )}
        </div>
      </div>
    </SearchModalShell>
  );
};

export default SearchModal;
